import * as titanClientModule from '@narvar/titan-client';
import type { TitanClient } from '@narvar/titan-client';
import { getRetailerFromUrl } from './getRetailerFromUrl';
import { clientLogger } from './logger';
import type { WorkflowEvents } from '../workflow';

type CreateTitanClient = typeof import('@narvar/titan-client').default;

/**
 * @narvar/titan-client is published as CommonJS (`exports.default = createTitanClient`).
 * Vite/Rollup production output can wrap `default` so `import x from` is not callable;
 * dev (esbuild) often still works — unwrap both shapes for QA/prod bundles.
 */
const getCreateTitanClient = (): CreateTitanClient | null => {
  const mod = titanClientModule as unknown as { default?: unknown };
  const first = mod.default;
  if (typeof first === 'function') return first as CreateTitanClient;
  if (first !== null && typeof first === 'object' && 'default' in first) {
    const second = (first as { default: unknown }).default;
    if (typeof second === 'function') return second as CreateTitanClient;
  }
  return null;
};

const createTitanClient = getCreateTitanClient();

export const sourceWriteKey = getRetailerFromUrl();

if (!createTitanClient && sourceWriteKey) {
  clientLogger.logError(
    new Error(
      'Titan client factory could not be resolved from @narvar/titan-client',
    ),
    'Titan client initialization',
  );
}

export const titanClient =
  sourceWriteKey && createTitanClient
    ? createTitanClient({
        url: import.meta.env.VITE_TITAN_URL,
        sourceWriteKey,
      })
    : null;

export type TitanEventType = Parameters<TitanClient['track']>[0]['event'];

export const logToTitan = ({
  event,
  properties,
}: Pick<Parameters<TitanClient['track']>[0], 'event' | 'properties'>) =>
  titanClient?.track({
    event,
    retailer_moniker: sourceWriteKey,
    channel: 'web',
    type: 'track',
    context: {
      app: {
        name: 'pdx',
      },
    },
    properties,
  });

export const workflowToTitanEvent: Partial<
  Record<WorkflowEvents['type'], TitanEventType>
> = {
  'xstate.init': 'checkout_started',
  'xstate.done.actor.getOrder': 'checkout_step_viewed',
  'xstate.done.actor.getServerState': 'checkout_step_viewed',
  updateAuthStatus: 'checkout_step_viewed',
  next: 'checkout_step_viewed',
  prev: 'checkout_step_viewed',
  goTo: 'checkout_step_viewed',
  complete: 'checkout_step_completed',
};
